/**
 * Утилиты для работы с приемками товаров
 * Группировка по контрагентам, определение статусов и т.д.
 */

/**
 * Определяет статус товара на основе результатов отправки
 * @param {Object} item - товар
 * @param {Array} sendResults - результаты отправки
 * @returns {Object} - статус товара
 */
export const getItemStatus = (item, sendResults = []) => {
    if (!item.purchaseOrderId) {
        return {
            isSent: false,
            hasError: false,
            isPending: true,
            status: 'pending',
            rowClass: 'not-sent',
            icon: '',
            allowEdit: true,
            allowDelete: true,
            moyskladId: null,
            errorMessage: null
        };
    }

    // Ищем актуальный результат для этого контрагента
    const latestResult = sendResults
        .filter(result => result.isLatest !== false)
        .find(result => result.purchaseOrderId === item.purchaseOrderId);

    if (latestResult) {
        if (latestResult.success) {
            return {
                isSent: true,
                hasError: false,
                isPending: false,
                status: 'success',
                rowClass: 'sent-success',
                icon: '✓',
                allowEdit: false,
                allowDelete: false,
                moyskladId: latestResult.moyskladId,
                sentAt: latestResult.sentAt,
                errorMessage: null
            };
        } else {
            return {
                isSent: false,
                hasError: true,
                isPending: false,
                status: 'error',
                rowClass: 'sent-error',
                icon: '⚠️',
                allowEdit: true,
                allowDelete: true,
                moyskladId: null,
                errorMessage: latestResult.error,
                sentAt: latestResult.sentAt
            };
        }
    }

    // Если нет результатов, товар ожидает отправки
    return {
        isSent: false,
        hasError: false,
        isPending: true,
        status: 'pending',
        rowClass: 'not-sent',
        icon: '',
        allowEdit: true,
        allowDelete: true,
        moyskladId: null,
        errorMessage: null
    };
};

/**
 * Вспомогательная функция для поиска контрагента по purchaseOrderId или agentId
 * @param {Array} counterparties - массив контрагентов
 * @param {String} purchaseOrderId - ID заказа
 * @param {String} agentId - ID контрагента (опционально)
 * @returns {Object|null} - контрагент или null
 */
export const findCounterpartyByOrderId = (counterparties, purchaseOrderId, agentId = null) => {
    if (!counterparties || !purchaseOrderId) return null;

    // Приоритет поиска: сначала по agentId (более точный), потом по purchaseOrderId
    if (agentId) {
        const agentMatch = counterparties.find(cp => cp.id === agentId);
        if (agentMatch) return agentMatch;
    }

    // Ищем контрагента, который соответствует заказу
    return counterparties.find(cp =>
        cp.purchaseOrderId === purchaseOrderId ||
        cp.orderIds?.includes(purchaseOrderId)
    );
};

/**
 * Группирует товары по контрагентам (заказам поставщиков)
 * @param {Array} items - товары
 * @param {Array} sendResults - результаты отправки
 * @param {Array} counterparties - массив контрагентов приемки
 * @returns {Array} - сгруппированные товары
 */
export const groupItemsByCounterparty = (items, sendResults = [], counterparties = []) => {
    const groups = new Map();

    // Сначала обрабатываем товары без заказа (всегда в начале)
    const itemsWithoutOrder = items.filter(item => !item.purchaseOrderId);
    if (itemsWithoutOrder.length > 0) {
        groups.set('no_order', {
            purchaseOrderId: null,
            purchaseOrderName: 'Без заказа',
            agentName: 'Не указан',
            status: 'pending',
            items: [],
            result: null
        });

        itemsWithoutOrder.forEach(item => {
            groups.get('no_order').items.push(item);
        });
    }

    // Создаем Map для отслеживания порядка появления purchaseOrderId
    const orderMap = new Map();

    items.forEach(item => {
        const key = item.purchaseOrderId;

        if (!key) {
            return; // Уже обработаны выше
        }

        // Запоминаем порядок первого появления purchaseOrderId
        if (!orderMap.has(key)) {
            orderMap.set(key, orderMap.size);
        }

        if (!groups.has(key)) {
            // Определяем статус группы на основе результатов отправки
            const itemStatus = getItemStatus(item, sendResults);
            const result = sendResults
                .filter(r => r.isLatest !== false)
                .find(r => r.purchaseOrderId === key);

            // Ищем контрагента в массиве counterparties с приоритетом по agentId
            const counterpartyFromList = findCounterpartyByOrderId(counterparties, key, item.agentId);

            // Определяем имя контрагента по приоритету:
            // 1. Из результатов отправки (sendResults)
            // 2. Из массива контрагентов приемки (counterparties)
            // 3. Из товара (item.agentName)
            // 4. Значение по умолчанию
            const agentName = result?.agentName ||
                             counterpartyFromList?.agentName ||
                             item.agentName ||
                             'Неизвестный контрагент';

            const agentId = result?.agentId ||
                           counterpartyFromList?.agentId ||
                           item.agentId;

            groups.set(key, {
                purchaseOrderId: key,
                purchaseOrderName: item.purchaseOrderName || result?.purchaseOrderName || `Заказ ${key}`,
                cmsOrderName: item.cmsOrderName || null,
                agentName: agentName,
                agentId: agentId,
                status: itemStatus.status,
                result: result,
                items: []
            });
        }

        groups.get(key).items.push(item);
    });

    // Сортируем группы по порядку появления purchaseOrderId
    const sortedGroups = Array.from(groups.entries())
        .sort(([keyA], [keyB]) => {
            // no_order всегда в начале
            if (keyA === 'no_order') return -1;
            if (keyB === 'no_order') return 1;

            // Сортируем по порядку появления
            const orderA = orderMap.get(keyA);
            const orderB = orderMap.get(keyB);
            return orderA - orderB;
        })
        .map(([_, group]) => group);

    return sortedGroups;
};

/**
 * Получает сводную статистику по приемке
 * @param {Array} items - товары
 * @param {Array} sendResults - результаты отправки
 * @returns {Object} - статистика
 */
export const getReceiptStats = (items, sendResults = []) => {
    const latestResults = sendResults.filter(result => result.isLatest !== false);
    const successCount = latestResults.filter(r => r.success).length;
    const errorCount = latestResults.filter(r => !r.success).length;

    const groupedItems = groupItemsByCounterparty(items, sendResults);
    const totalGroups = groupedItems.length;
    const successGroups = groupedItems.filter(group => group.status === 'success').length;
    const errorGroups = groupedItems.filter(group => group.status === 'error').length;
    const pendingGroups = groupedItems.filter(group => group.status === 'pending').length;

    return {
        totalItems: items.length,
        totalGroups,
        successGroups,
        errorGroups,
        pendingGroups,
        successCount,
        errorCount,
        allSuccess: successGroups === totalGroups && totalGroups > 0,
        hasErrors: errorGroups > 0,
        isPartiallySuccessful: successGroups > 0 && errorGroups > 0
    };
};

/**
 * Получает CSS классы для строки товара
 * @param {Object} itemStatus - статус товара
 * @returns {Object} - CSS классы
 */
export const getItemRowStyles = (itemStatus) => {
    const baseStyles = {
        opacity: 1,
        transition: 'all 0.3s ease',
        cursor: itemStatus.allowEdit ? 'default' : 'not-allowed'
    };

    switch (itemStatus.status) {
        case 'success':
            return {
                ...baseStyles,
                backgroundColor: '#e8f5e8',
                border: '1px solid #c8e6c9',
                color: '#2e7d32'
            };
        case 'error':
            return {
                ...baseStyles,
                backgroundColor: '#ffebee',
                border: '1px solid #ffcdd2',
                color: '#c62828'
            };
        case 'pending':
        default:
            return {
                ...baseStyles,
                backgroundColor: 'transparent',
                border: '1px solid #f0f0f0'
            };
    }
};

/**
 * Форматирует текст статуса для отображения
 * @param {Object} itemStatus - статус товара
 * @returns {string} - текст статуса
 */
export const getStatusText = (itemStatus) => {
    switch (itemStatus.status) {
        case 'success':
            return `Отправлено в МойСклад ${itemStatus.moyskladId ? `(ID: ${itemStatus.moyskladId})` : ''}`;
        case 'error':
            return `Ошибка отправки: ${itemStatus.errorMessage || 'Неизвестная ошибка'}`;
        case 'pending':
            return 'Ожидает отправки';
        default:
            return 'Неизвестный статус';
    }
};

/**
 * Проверяет, можно ли отправить приемку
 * @param {Array} items - товары
 * @param {Array} sendResults - результаты отправки
 * @returns {Object} - результат проверки
 */
export const canSendReceipt = (items, sendResults = []) => {
    if (items.length === 0) {
        return {
            canSend: false,
            reason: 'Добавьте хотя бы один товар'
        };
    }

    const groupedItems = groupItemsByCounterparty(items, sendResults);
    const successGroups = groupedItems.filter(group => group.status === 'success');

    if (successGroups.length === groupedItems.length && groupedItems.length > 0) {
        return {
            canSend: false,
            reason: 'Все товары уже успешно отправлены',
            alreadySent: true
        };
    }

    const pendingItems = items.filter(item =>
        getItemStatus(item, sendResults).status === 'pending'
    );

    const errorItems = items.filter(item =>
        getItemStatus(item, sendResults).status === 'error'
    );

    if (pendingItems.length === 0 && errorItems.length === 0) {
        return {
            canSend: false,
            reason: 'Нет товаров для отправки'
        };
    }

    return {
        canSend: true,
        itemsToSend: [...pendingItems, ...errorItems],
        itemsToRetry: errorItems.length,
        itemsPending: pendingItems.length
    };
};